import { useState, useCallback, useRef } from "react";

export const useStreamingChat = () => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");

  // CRITICAL FIX: Store the final streamed content
  const finalStreamedContentRef = useRef("");

  const sendStreamingMessage = useCallback(
    async (sessionId, message, options = {}) => {
      setIsStreaming(true);
      setStreamingContent("");
      finalStreamedContentRef.current = ""; // Reset

      let finalContent = "";

      try {
        console.log(
          "Starting streaming request to:",
          `/api/chat/sessions/${sessionId}/messages/stream`
        );

        const response = await fetch(
          `/api/chat/sessions/${sessionId}/messages/stream`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              Accept: "text/event-stream",
              "Cache-Control": "no-cache",
            },
            body: JSON.stringify({
              message,
              options,
              selectedModel: options.selectedModel
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("text/event-stream")) {
          throw new Error("Server did not return event stream");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              console.log("Stream reading completed");
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            const parts = buffer.split("\n\n");
            buffer = parts.pop() || "";

            for (const part of parts) {
              const lines = part.split("\n");

              for (const line of lines) {
                if (line.startsWith("data: ")) {
                  const dataStr = line.slice(6).trim();

                  if (!dataStr) continue;

                  try {
                    const data = JSON.parse(dataStr);

                    if (data.type === "content") {
                      finalContent = data.fullContent || finalContent;
                      setStreamingContent(finalContent);
                      // CRITICAL: Store the content as we stream
                      finalStreamedContentRef.current = finalContent;
                    } else if (data.type === "complete") {
                      console.log(
                        "Stream completed - preserving final content:",
                        finalContent.substring(0, 100) + "..."
                      );

                      // CRITICAL: Store the final content before returning
                      finalStreamedContentRef.current = finalContent;

                      // Return completion data but don't change streaming state yet
                      return {
                        ...data,
                        finalContent: finalContent,
                        streamingComplete: true,
                      };
                    } else if (data.type === "error") {
                      throw new Error(data.error);
                    } else if (data.type === "status") {
                      const statusContent = data.content || "Loading...";
                      setStreamingContent(statusContent);
                      finalStreamedContentRef.current = statusContent;
                    }
                  } catch (parseError) {
                    console.warn("Failed to parse SSE data:", parseError);
                  }
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }

        // Store final content before returning
        finalStreamedContentRef.current = finalContent;
        return { finalContent: finalContent, streamingComplete: true };
      } catch (error) {
        console.error("Streaming error:", error);
        setIsStreaming(false);
        setStreamingContent("");
        finalStreamedContentRef.current = "";
        throw error;
      }
    },
    []
  );

  // FIXED: Preserve the final content when completing
  const completeStreaming = useCallback(() => {
    console.log("🏁 completeStreaming() CALLED:");
    console.log(
      "  - Current streamingContent length:",
      streamingContent?.length || 0
    );
    console.log(
      "  - Current streamingContent preview:",
      streamingContent?.substring(0, 100) + "..."
    );
    console.log(
      "  - finalStreamedContentRef length:",
      finalStreamedContentRef.current?.length || 0
    );
    console.log(
      "  - finalStreamedContentRef preview:",
      finalStreamedContentRef.current?.substring(0, 100) + "..."
    );

    // CRITICAL: Don't clear streamingContent - keep the final content visible
    console.log("🔄 Setting isStreaming to false...");
    setIsStreaming(false);
    console.log(
      "✅ completeStreaming() COMPLETED - streamingContent should remain intact"
    );
    // streamingContent stays as is with the final content
  }, [streamingContent]);

  const clearStreamingContent = useCallback(() => {
    console.log("🧹 clearStreamingContent() CALLED:");
    console.log(
      "  - Clearing streamingContent (was",
      streamingContent?.length || 0,
      "chars)"
    );
    console.log(
      "  - Clearing finalStreamedContentRef (was",
      finalStreamedContentRef.current?.length || 0,
      "chars)"
    );
    setStreamingContent("");
    setIsStreaming(false);
    finalStreamedContentRef.current = "";
    console.log("✅ All streaming content cleared");
  }, [streamingContent]);

  // NEW: Getter for the final streamed content
  const getFinalStreamedContent = useCallback(() => {
    return finalStreamedContentRef.current;
  }, []);

  return {
    isStreaming,
    streamingContent,
    sendStreamingMessage,
    completeStreaming,
    clearStreamingContent,
    getFinalStreamedContent, // NEW: Expose the final content
  };
};
